import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { api } from '../../lib/api';
import { ConditionAnswers } from '../../lib/condition-answers';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, ErrorText } from '../../ui';

interface RefreshState { aggiornando: boolean; versione: string | null; errore: string | null }
const queues = new Map<string, ConditionAnswers>();

export function useConditionAnswers() {
  const { tenant, me } = useTenant();
  const outbox = useMemo(() => {
    const key = `${me.id}:${tenant.id}`;
    let queue = queues.get(key);
    if (!queue) {
      // Accesso differito: se lo storage non è disponibile la risposta resta visibile con l'errore.
      const storage = {
        get length() { return localStorage.length; },
        key: (i: number) => localStorage.key(i),
        getItem: (k: string) => localStorage.getItem(k),
        setItem: (k: string, v: string) => localStorage.setItem(k, v),
        removeItem: (k: string) => localStorage.removeItem(k),
        clear: () => {},
      };
      queue = new ConditionAnswers(storage, me.id, tenant.id,
        (condition, body) => api(`/tenants/${tenant.id}/model/answer/${encodeURIComponent(condition)}?background=1`, { body }),
        async (work) => { if (navigator.locks) await navigator.locks.request(`bk.condition-answers:${key}`, work); else await work(); });
      queues.set(key, queue);
    }
    return queue;
  }, [me.id, tenant.id]);
  const snapshot = useSyncExternalStore(outbox.subscribe, outbox.getSnapshot);
  useEffect(() => {
    const release = outbox.retain();
    const stored = (event: StorageEvent) => { if (event.key?.startsWith(outbox.prefix)) outbox.retry(); };
    window.addEventListener('storage', stored);
    window.addEventListener('online', outbox.retry);
    return () => { release(); window.removeEventListener('storage', stored); window.removeEventListener('online', outbox.retry); };
  }, [outbox]);
  return { outbox, ...snapshot };
}

/** Resta montato anche dopo l'ultima domanda e cambiando pagina: invii, errori e aggiornamento delle viste. */
export function ConditionAnswersSync() {
  const { tenant } = useTenant();
  const { outbox, pending, error, revision } = useConditionAnswers();
  const client = useQueryClient();
  const previous = useRef<string | null>(null);
  const q = useTenantQuery<RefreshState>(['model-refresh'], '/model/refresh', { refetchInterval: (s) => pending.length || s?.aggiornando ? 1500 : false });
  useEffect(() => {
    void client.invalidateQueries({ queryKey: ['tenant', tenant.id, 'conditions'] });
    void client.invalidateQueries({ queryKey: ['tenant', tenant.id, 'model-refresh'] });
  }, [client, tenant.id, revision]);
  useEffect(() => {
    if (!q.data?.versione || q.data.aggiornando || previous.current === q.data.versione) return;
    previous.current = q.data.versione;
    for (const key of ['model', 'model-scopes', 'data-model', 'archive', 'roles', 'procedures']) void client.invalidateQueries({ queryKey: ['tenant', tenant.id, key] });
  }, [client, tenant.id, q.data]);
  const retry = async () => {
    outbox.retry();
    if (q.data?.errore) {
      try { await api(`/tenants/${tenant.id}/model/refresh`, { body: {} }); } catch { /* la lettura dello stato permette di riprovare */ }
      void q.refetch();
    }
  };
  if (!pending.length && !error && !q.data?.aggiornando && !q.data?.errore) return null;
  return (
    <div className="fixed bottom-4 left-4 z-[60] max-w-sm rounded-xl bg-white p-3 text-xs shadow-lg">
      {(error || q.data?.errore) ? <><ErrorText>{error ?? q.data?.errore}</ErrorText><p className="mt-1">{pending.length ? "Le risposte in attesa sono conservate nel browser. Puoi continuare." : "Le risposte già salvate restano conservate."}</p><Button variant="ghost" onClick={() => void retry()}>Riprova</Button></> : <p role="status">{pending.length ? `Salvo ${pending.length === 1 ? 'la risposta' : `le ${pending.length} risposte`}… Puoi continuare.` : 'Aggiorno il gestionale in background…'}</p>}
    </div>
  );
}
